import { useState, useCallback } from "react";
import { addTokenToHeader } from "@utils/rtkApiHelpers";
import { useToastNotification } from "@hooks";
import useSdkUtils from "@hooks/useSdkUtils";
import { isZoomApp } from "@utils/helpers";

const useExportAgendaPptx = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { showSuccessToast, showErrorToast } = useToastNotification();
  const { openUrl } = useSdkUtils();

  const exportAgenda = useCallback(
    async (agendaId) => {
      if (isExporting) return;

      setIsExporting(true);
      try {
        const baseUrl = process.env.REACT_APP_ZOOMAPP_BACKEND_BASE_URL;
        const headers = addTokenToHeader({});

        if (isZoomApp) {
          // Zoom App: get a signed token, then open download URL in external browser
          const response = await fetch(
            `${baseUrl}/agenda/${agendaId}/export/pptx/signed-url`,
            { method: "GET", headers },
          );

          if (!response.ok) {
            throw new Error(`Export failed with status ${response.status}`);
          }

          const { content } = await response.json();
          const downloadUrl = `${baseUrl}/agenda/export/pptx/download/${content.token}`;
          await openUrl(downloadUrl);
          showSuccessToast("Opening download in your browser");
        } else {
          // Web: download directly via blob
          const response = await fetch(
            `${baseUrl}/agenda/${agendaId}/export/pptx`,
            { method: "GET", headers },
          );

          if (!response.ok) {
            throw new Error(`Export failed with status ${response.status}`);
          }

          const blob = await response.blob();

          // Extract filename from Content-Disposition header
          const contentDisposition = response.headers.get(
            "Content-Disposition",
          );
          const fallbackDate = new Date().toISOString().split("T")[0];
          let filename = `Townhall.pro_Agenda_${fallbackDate}.pptx`;
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) {
              filename = match[1];
            }
          }

          // Trigger browser download
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          showSuccessToast("Agenda exported to PowerPoint");
        }
      } catch (error) {
        console.error("[useExportAgendaPptx] Export failed:", error);
        showErrorToast("Failed to export agenda. Please try again.");
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, showSuccessToast, showErrorToast, openUrl],
  );

  return { exportAgenda, isExporting };
};

export default useExportAgendaPptx;
